import React, { useState } from "react"
import toast from "react-hot-toast"
import { post, remove } from "./../utils/apiClient"

const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"]

const fmt = (d) => {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, "0")
  const day = String(d.getDate()).padStart(2, "0")
  return `${y}-${m}-${day}`
}

const STATUS_STYLES = {
  success: { bg: "#16a34a", border: "#15803d", text: "#fff", label: "Success" },
  failed: { bg: "#dc2626", border: "#b91c1c", text: "#fff", label: "Failed" },
  awaiting: {
    bg: "#0ea5e9",
    border: "#0284c7",
    text: "#fff",
    label: "Awaiting",
  },
  missing_files: {
    bg: "#ca8a04",
    border: "#a16207",
    text: "#fff",
    label: "Missing Files",
  },
}

const LEGEND = [
  { status: "success", label: "Success", bg: "#16a34a" },
  { status: "failed", label: "Failed", bg: "#dc2626" },
  { status: "awaiting", label: "Awaiting", bg: "#0ea5e9" },
  { status: "missing_files", label: "Missing Files", bg: "#ca8a04" },
]

const MonthGrid = ({
  year,
  month,
  today,
  startDate,
  maxDate,
  scheduledDates,
  onDayClick,
  loadingDate,
}) => {
  const firstDay = new Date(year, month, 1).getDay()
  const daysInMonth = new Date(year, month + 1, 0).getDate()
  const monthName = new Date(year, month, 1).toLocaleString("default", {
    month: "long",
    year: "numeric",
  })

  const cells = []
  for (let i = 0; i < firstDay; i++) cells.push(null)
  for (let d = 1; d <= daysInMonth; d++) cells.push(d)

  return (
    <div className="sp-month">
      <div className="sp-month-name">{monthName}</div>
      <div className="sp-day-headers">
        {DAYS.map((d) => (
          <div key={d} className="sp-day-header">
            {d}
          </div>
        ))}
      </div>
      <div className="sp-day-grid">
        {cells.map((day, i) => {
          if (!day) return <div key={`e-${i}`} />
          const date = new Date(year, month, day)
          const key = fmt(date)
          const isToday = fmt(date) === fmt(today)
          const isBeforeStart = date < startDate
          const isFuture = date > maxDate
          const isDisabled = isBeforeStart || isFuture
          const isPast = date < today
          const entry = scheduledDates.find((s) => s.date === key)
          const isScheduled = !!entry
          const statusStyle = entry
            ? STATUS_STYLES[entry.status] || STATUS_STYLES.awaiting
            : null
          const isLoading = key === loadingDate

          let bg = "transparent",
            color = "#333",
            border = "1px solid transparent",
            cursor = "pointer",
            fontWeight = 400
          if (isLoading) {
            bg = "#e2e8f0"
            color = "#94a3b8"
            cursor = "wait"
          } else if (isDisabled) {
            color = "#ccc"
            cursor = "default"
          } else if (isPast && isScheduled) {
            bg = statusStyle.bg
            color = statusStyle.text
            border = `1px solid ${statusStyle.border}`
            fontWeight = 700
            cursor = "default"
          } else if (isPast) {
            color = "#ccc"
            cursor = "default"
          } else if (isScheduled) {
            bg = statusStyle.bg
            color = statusStyle.text
            border = `1px solid ${statusStyle.border}`
            fontWeight = 700
          } else if (isToday) {
            border = "1px solid #333"
            fontWeight = 700
          }

          return (
            <div
              key={key}
              className="sp-day-cell"
              onClick={() =>
                !isDisabled &&
                !isPast &&
                !isLoading &&
                onDayClick(key, isScheduled)
              }
              title={
                isLoading
                  ? "Saving..."
                  : isScheduled
                    ? `${statusStyle.label} — click to remove`
                    : isDisabled || isPast
                      ? ""
                      : "Click to schedule"
              }
              style={{ background: bg, color, border, cursor, fontWeight }}
              onMouseEnter={(e) => {
                if (!isDisabled && !isPast && !isLoading)
                  e.currentTarget.style.background = isScheduled
                    ? statusStyle.border
                    : "#f0f9ff"
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = bg
              }}
            >
              {isLoading ? <span className="sp-cell-spinner" /> : day}
            </div>
          )
        })}
      </div>
    </div>
  )
}

const SchedulePopup = ({
  folderName,
  scheduledDates,
  scheduleId,
  onClose,
  onScheduleChange,
  authUser,
}) => {
  const [deleteScheduleConfirm, setDeleteScheduleConfirm] = useState(null)
  const [loadingDate, setLoadingDate] = useState(null)
  const [deletingDate, setDeletingDate] = useState(null)

  //const today = new Date(2025, 3, 1) // DEBUG: hardcoded to Jan 1
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const startDate = new Date(today)
  startDate.setDate(today.getDate() - 7)
  const maxDate = new Date(today)
  maxDate.setDate(today.getDate() + 60)

  const baseMonths = [0, 1, 2]
    .map((offset) => {
      const d = new Date(today.getFullYear(), today.getMonth() + offset, 1)
      return { year: d.getFullYear(), month: d.getMonth() }
    })
    .filter(({ year, month }) => {
      const firstOfMonth = new Date(year, month, 1)
      return firstOfMonth <= maxDate
    })

  const prevMonthDate = new Date(today.getFullYear(), today.getMonth() - 1, 1)
  const prevYear = prevMonthDate.getFullYear()
  const prevMonth = prevMonthDate.getMonth()
  const hasPrevMonthSchedule = scheduledDates.some(({ date }) => {
    const d = new Date(date)
    return d.getFullYear() === prevYear && d.getMonth() === prevMonth
  })

  const months =
    startDate.getMonth() !== today.getMonth() && hasPrevMonthSchedule
      ? [{ year: prevYear, month: prevMonth }, ...baseMonths]
      : baseMonths

  const formatDisplay = (date) => {
    const [y, m, d] = date.split("-")
    return `${m}/${d}/${y}`
  }

  const handleAdd = async (date) => {
    setLoadingDate(date)
    try {
      const res = await post("/schedule", {
        ref: authUser?.faxNumber,
        subfolder: folderName,
        faxDates: [date],
      })
      const updated = Object.entries(res.faxDates || {}).map(([d, s]) => ({
        date: d,
        status: s.toLowerCase(),
      }))
      onScheduleChange(updated, res.id)
      toast.success(`Schedule set for ${date}`)
    } catch (err) {
      toast.error(typeof err === "string" ? err : "Failed to schedule date")
    } finally {
      setLoadingDate(null)
    }
  }

  const handleDelete = async () => {
    const { date } = deleteScheduleConfirm
    setDeletingDate(date)
    setDeleteScheduleConfirm(null)
    try {
      const res = await remove(`/schedule/${scheduleId}/date?date=${date}`)
      if (res.scheduleDeleted) {
        onScheduleChange([], null)
      } else {
        const updated = Object.entries(res.schedule?.faxDates || {}).map(
          ([d, s]) => ({ date: d, status: s.toLowerCase() }),
        )
        onScheduleChange(updated, scheduleId)
      }
      setDeletingDate(null)
      toast.success(`Schedule removed for ${date}`)
    } catch (err) {
      toast.error(typeof err === "string" ? err : "Failed to remove date")
      setDeletingDate(null)
    }
  }

  return (
    <>
      <div className="sp-overlay">
        <div className="sp-popup">
          {/* Header */}
          <div className="sp-header">
            <div className="sp-header-left">
              <span className="sp-header-icon">📅</span>
              <span className="sp-header-title">
                Manage Schedule for {folderName}
              </span>
            </div>
            <div className="sp-header-right">
              {LEGEND.map(({ label, bg }) => (
                <div key={label} className="sp-legend-item">
                  <span className="sp-legend-dot" style={{ background: bg }} />
                  {label}
                </div>
              ))}
              <button className="sp-close-btn" onClick={onClose}>
                ✕
              </button>
            </div>
          </div>

          {/* Calendar grid */}
          <div className="sp-calendar-body">
            {months.map(({ year, month }, idx) => (
              <React.Fragment key={`${year}-${month}`}>
                {idx > 0 && <div className="sp-divider" />}
                <MonthGrid
                  year={year}
                  month={month}
                  today={today}
                  startDate={startDate}
                  maxDate={maxDate}
                  scheduledDates={scheduledDates}
                  loadingDate={loadingDate || deletingDate}
                  onDayClick={(key, isScheduled) =>
                    isScheduled
                      ? setDeleteScheduleConfirm({ date: key })
                      : handleAdd(key)
                  }
                />
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>

      {/* Delete Confirmation */}
      {deleteScheduleConfirm && (
        <div className="sp-confirm-overlay">
          <div className="sp-confirm-box">
            {/* <div className="sp-confirm-header sp-confirm-header--danger">
              <span>🗑️</span>
              <span>Remove Schedule</span>
            </div> */}
            <div className="sp-confirm-body">
              <p className="sp-confirm-label">🗑️ Remove schedule for</p>
              <p className="sp-confirm-date">{formatDisplay(deleteScheduleConfirm.date)}</p>
              <div className="sp-confirm-actions">
                <button
                  className="sp-btn sp-btn--cancel"
                  onClick={() => setDeleteScheduleConfirm(null)}
                >
                  Cancel
                </button>
                <button
                  className="sp-btn sp-btn--danger"
                  onClick={handleDelete}
                >
                  Yes, Remove
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default SchedulePopup
